import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
  Stack,
  Card,
  CardContent,
  FormGroup,
  FormLabel,
  Chip,
  Autocomplete
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import StoreIcon from '@mui/icons-material/Store';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { createBranch } from '../branchesApi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAllFilters } from '../api/branchFiltersApi';
import { fetchCities } from '../citiesApi';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function LocationMarker({ position, setPosition }) {
  const map = useMap();
  
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  return position === null ? null : (
    <Marker position={position} draggable={true} eventHandlers={{
      dragend: (e) => {
        setPosition(e.target.getLatLng());
      }
    }}>
    </Marker>
  );
}

export default function AddBranch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phoneNumber: '',
    facebookUrl: '',
    instagramUrl: '',
    description: '',
    deliveryRangeKm: 0,
    isActive: true,
    openingTime: '08:00',
    closingTime: '23:00',
    deliveryFeeType: 'fixedAmount',
    deliveryFeeValue: 0,
    image: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [position, setPosition] = useState({ lat: 15.3694, lng: 44.1910 });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);

  // ✅ NEW: Cities state
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);

  useEffect(() => {
    fetchAllFilters().then(setFilters);
    // ✅ NEW: Load cities
    fetchCities().then(setCities).catch(err => console.error('Error loading cities:', err));
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (name === 'phoneNumber') {
      if ((/^\d*$/.test(value)) && value.length <= 9) {
        setFormData({ ...formData, [name]: value });
      }
    } else if (name === 'deliveryFeeValue') {
      setFormData({ ...formData, [name]: Math.max(0, Number(value)) });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
        },
        (err) => {
          console.error(err);
          setNotification({ open: true, message: t('Error_Getting_Location'), severity: 'error' });
        }
      );
    } else {
      setNotification({ open: true, message: t('Geolocation_Not_Supported'), severity: 'error' });
    }
  };

  const handleFilterChange = (id) => {
    setSelectedFilters((prev) =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        phoneNumber: `+967${formData.phoneNumber}`,
        latitude: position.lat,
        longitude: position.lng,
        filterIds: selectedFilters,
        // ✅ NEW: Include selected city IDs
        cityIds: selectedCities.map(city => city.Id)
      };
      await createBranch(payload);
      setNotification({ open: true, message: t('BranchCreatedSuccess'), severity: 'success' });
      setTimeout(() => navigate('/admin/branches'), 1500);
    } catch (error) {
      console.error('Error creating branch:', error);
      setNotification({ open: true, message: t('BranchCreateFail'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StoreIcon fontSize="large" color="primary" />
            {t('CreateNewBranch')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t('AddBranchDesc')}
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/branches')}
        >
          {t('BackToList')}
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Left Column: Branch Details */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ height: '100%', boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary" sx={{ mb: 3 }}>
                  {t('BranchInformation')}
                </Typography>
                
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label={t('BranchName')}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    placeholder="e.g. Main Street Branch"
                  />
                  
                  <TextField
                    fullWidth
                    label={t('Phone_Number')}
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="77xxxxxxx"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box sx={{ 
                            bgcolor: 'primary.main', 
                            color: 'white', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            mr: 1,
                            fontWeight: 'bold'
                          }}>
                            +967
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    helperText={`${formData.phoneNumber.length}/9 ${t('Digits')}`}
                    error={formData.phoneNumber.length > 0 && formData.phoneNumber.length !== 9}
                  />

                  <TextField
                    fullWidth
                    label={t('Address')}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Full street address..."
                  />

                  <TextField
                    fullWidth
                    label={t('Description')}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder="Branch description..."
                  />

                  {/* Image Upload Field */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('BranchImage')}
                    </Typography>
                    <Button variant="contained" component="label">
                      {t('UploadImage')}
                      <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </Button>
                    {imagePreview && (
                      <Box mt={2}>
                        <img src={imagePreview} alt="Preview" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, border: '1px solid #ccc' }} />
                      </Box>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('OpeningTime')}
                        name="openingTime"
                        type="time"
                        value={formData.openingTime}
                        onChange={handleChange}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('ClosingTime')}
                        name="closingTime"
                        type="time"
                        value={formData.closingTime}
                        onChange={handleChange}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('DeliveryRange')}
                        name="deliveryRangeKm"
                        type="number"
                        value={formData.deliveryRangeKm}
                        onChange={handleChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{t('Km')}</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isActive}
                            onChange={handleChange}
                            name="isActive"
                            color="success"
                          />
                        }
                        label={
                          <Typography variant="body1" fontWeight="medium">
                            {t('ActiveStatus')}
                          </Typography>
                        }
                        sx={{ ml: 1 }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('SelectedCoordinates')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          label={t('Latitude')}
                          value={position.lat.toFixed(6)}
                          size="small"
                          InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          label={t('Longitude')}
                          value={position.lng.toFixed(6)}
                          size="small"
                          InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Social Media Section */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t('socialMedia')}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Facebook URL"
                          name="facebookUrl"
                          value={formData.facebookUrl}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><FacebookIcon color="primary" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Instagram URL"
                          name="instagramUrl"
                          value={formData.instagramUrl}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><InstagramIcon color="secondary" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Delivery Fee Section */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    {t('DeliveryFee')}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={7}>
                      <TextField
                        select
                        fullWidth
                        label={t('DeliveryFeeType')}
                        name="deliveryFeeType"
                        value={formData.deliveryFeeType}
                        onChange={handleChange}
                        SelectProps={{ native: true }}
                        variant="outlined"
                      >
                        <option value="fixedAmount">{t('FixedAmount')}</option>
                        <option value="byKm">{t('ByKm')}</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label={formData.deliveryFeeType === 'byKm' ? t('FeePerKm') : t('FixedFeeAmount')}
                        name="deliveryFeeValue"
                        type="number"
                        value={formData.deliveryFeeValue}
                        onChange={handleChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{formData.deliveryFeeType === 'byKm' ? t('PerKm') : t('Currency')}</InputAdornment>
                        }}
                        variant="outlined"
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {/* ✅ NEW: Delivery Cities Section */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalShippingIcon color="primary" />
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    {t('DeliveryCities')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('DeliveryCitiesDesc')}
                </Typography>

                {/* ✅ Select All Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cities.length > 0 && selectedCities.length === cities.length}
                      indeterminate={selectedCities.length > 0 && selectedCities.length < cities.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCities([...cities]);
                        } else {
                          setSelectedCities([]);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="bold">
                      {t('SelectAllCities')} ({cities.length})
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Autocomplete
                  multiple
                  options={cities}
                  getOptionLabel={(option) => option.Name || option.name || ''}
                  value={selectedCities}
                  onChange={(event, newValue) => setSelectedCities(newValue)}
                  isOptionEqualToValue={(option, value) => (option.Id || option.id) === (value.Id || value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('SelectCities')}
                      placeholder={t('SearchCities')}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.Id || option.id}
                        label={option.Name || option.name}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))
                  }
                  noOptionsText={t('NoCitiesFound')}
                  sx={{ mb: 1 }}
                />
                {selectedCities.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedCities.length} {t('CitiesSelected')}
                  </Typography>
                )}
                {selectedCities.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    {t('NoCitiesSelectedWarning')}
                  </Typography>
                )}

                
                {/* Filters Selection */}
                <Divider sx={{ my: 3 }} />
                <FormLabel component="legend">{t('Filters')}</FormLabel>
                <FormGroup row>
                  {filters.map((filter) => (
                    <FormControlLabel
                      key={filter.Id}
                      control={
                        <Checkbox
                          checked={selectedFilters.includes(filter.Id)}
                          onChange={() => handleFilterChange(filter.Id)}
                        />
                      }
                      label={filter.name}
                    />
                  ))}
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Map */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card sx={{ height: '100%', boxShadow: 3, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {t('LocationPin')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('MapInstruction')}
                    </Typography>
                  </Box>
                  <Button 
                    variant="contained" 
                    color="info" 
                    size="small" 
                    startIcon={<MyLocationIcon />}
                    onClick={handleGetCurrentLocation}
                    sx={{ borderRadius: 20, textTransform: 'none' }}
                  >
                    {t('LocateMe')}
                  </Button>
                </Box>
                
                <Box sx={{ flexGrow: 1, minHeight: 500, position: 'relative' }}>
                  <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }} attributionControl={false}>
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker position={position} setPosition={setPosition} />
                  </MapContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Footer Actions */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => navigate('/admin/branches')}
                sx={{ px: 4 }}
              >
                {t('Cancel')}
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={loading}
                startIcon={loading ? null : <SaveIcon />}
                sx={{ px: 5, py: 1.5, fontWeight: 'bold', boxShadow: 4 }}
              >
                {loading ? t('CreatingBranch') : t('CreateBranch')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}